import React, {
	useState,
	useEffect,
	cloneElement
} from 'react';
import classNames from 'classnames';
import uniqueId from 'uniqid';

const ns = `tabs`;

const Tabs = ({
	children,
	modifier,
	className,
	noFocus,
	setExternalState,
}) => {
	const tabs = Array.isArray(children)
		? children.filter(Boolean)
		: [children];
	const selected = tabs.find(tab => { return tab.props.selected; }) || tabs[0];
	const [state, setState] = useState({
		selected,
	});

	let activeLink;

	useEffect(() => {
		if (noFocus) return;
		if (activeLink) {
			activeLink.focus();
			activeLink.scrollIntoView({ block: 'nearest', inline: 'end' });
		}
	});

	const selectTab = tab => {
		if (!tab) return;
		setState({
			selected: tab,
		});
		setExternalState?.(() => tab);
	};

	const previousTab = tab => {
		const index = tabs.indexOf(tab);
		if (index > 0) selectTab(tabs[index - 1]);
	};

	const nextTab = tab => {
		const index = tabs.indexOf(tab);
		if (index < tabs.length - 1) selectTab(tabs[index + 1]);
	};

	const handleClick = (e, tab) => {
		e.preventDefault();
		selectTab(tab);
	};

	const handleKeyup = (e, tab) => {
		e.preventDefault();
		if (e.which === 13) selectTab(tab);
		else if (e.which === 37) previousTab(tab);
		else if (e.which === 39) nextTab(tab);
	};

	useEffect(() => {
		if (setExternalState && selected.key !== state.selected.key) {
			setState({
				selected,
			});
			setExternalState(prev => prev || selected);
		}
	}, [selected, setExternalState]); // eslint-disable-line

	const TabControls = () => {
		return (
			<ul className={`${ ns }__controls`} role={`tablist`}>
				{tabs.map((tab, i) => {
					return (
						<li key={uniqueId('bb')} role={`presentation`}>
							<a
								id={`tab_${ i }`}
								href={`#tabpanel_${ i }`}
								role={`tab`}
								data-label={typeof tab.props.title === 'string' ? tab.props.title : undefined}
								aria-controls={`tab_${ i }`}
								aria-selected={tab.key === state.selected.key}
								tabIndex={tab.key === state.selected.key ? 0 : -1}
								onClick={e => { handleClick(e, tab); }}
								onKeyUp={e => { handleKeyup(e, tab); }}
								ref={link => {
									if (tab === state.selected) {
										activeLink = link;
									}
								}}
							>
								{tab.props.title}
							</a>
						</li>
					);
				})}
			</ul>
		);
	};

	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }--${ modifier }`]: modifier,
		[className]: className,
	});

	return (
		<div className={rootClassnames}>
			<TabControls />
			<div className={`${ ns }__content`}>
				{tabs.map((tab, i) => {
					return (
						cloneElement(tab, {
							index: i,
							selected: () => {
								return tab.key === state.selected.key;
							},
						})
					);
				})}
			</div>
		</div>
	);
};

export default Tabs;
